<template>
  <div
    :id="`product-card-${computedProduct.objectID}`"
    class="product-card"
    :class="[
      $blockClass(blockClass, 'product-card'),
      {
        [$class('loading')]: !computedProduct,
        'product-card--large': large,
        'show-secondary-image': showSecondaryImage,
      },
    ]"
    :data-product-page="computedProduct.page"
  >
    <div
      class="product-card__thumbnail"
      @mouseover="cardHover = true"
      @mouseleave="cardHover = false"
      @mouseenter="loadSecondaryImage = true"
    >
      <a
        :href="productUrl"
        @click="selectProduct"
      >
        <span
          v-if="badge && badge.label"
          class="product-card__badge text-label-s-desktop text-label-xs"
          :class="{
            'product-card__badge--sale': badge.type === 'sale'
          }"
          :style="{
            backgroundColor: badge.bgColor,
            color: badge.color,
          }"
          v-text="badge.label"
        />

        <responsive-image
          :key="`${imageKey}-primary`"
          class="product-card__primary-image"
          :class="{
            'product-card__big-image': bigCard
          }"
          :image="imagePrimary"
          :alt="imagePrimaryTitle"
          block-class="product-card"
          min-max="200-500"
          ratio="4:5"
          :style="{ animationDelay: animationDelay }"
        />

        <responsive-image
          v-if="loadSecondaryImage && imageSecondary && placement !== 'wiyw'"
          :key="`${imageKey}-secondary`"
          class="product-card__secondary-image"
          :class="{
            'product-card__big-image': bigCard
          }"
          :image="imageSecondary"
          :alt="imageSecondaryTitle"
          block-class="product-card"
          min-max="200-500"
          :padding="false"
          ratio="4:5"
          @image-loaded="showSecondaryImage = true"
        />
      </a>

      <template v-if="mergedProduct?.variants?.length">
        <template v-if="!isMobile && placement !== 'cart-drawer' && placement !== 'wiyw'">
          <transition name="slide-up">
            <div
              v-show="showQuickAdd && cardHover && mergedProduct?.variants?.length > 1"
              class="product-card__size-selector"
            >
              <size-selector
                :current-variant="currentVariantId"
                primary-vector="Waist"
                :product="mergedProduct"
                :variants="mergedProduct.variants"
                @product-variant-update="updateVariant"
              />
            </div>
          </transition>
        </template>

        <template v-else>
          <div
            v-if="showQuickAdd"
            class="product-card__quick-add-mobile"
          >
            <button
              :aria-label="$string('product.quick_view.add_to_bag')"
              class="product-card__quick-add-mobile-button"
              type="button"
              @click.prevent="handleAddToCart"
              @mousedown="loadComponent('quick-view')"
              @touchstart="loadComponent('quick-view')"
            >
              <icon-bag />
            </button>
          </div>
        </template>
      </template>
    </div>

    <div
      v-if="placement !== 'wiyw'"
      class="product-card__footer"
      :class="$blockClass(blockClass, 'product-card-footer')"
    >
      <p
        class="
          product-card__title
          text-body-3xs
          text-body-2xs-desktop
          text-center
        "
        :class="$blockClass(blockClass, 'product-card-title')"
        :style="{ animationDelay: animationDelay }"
        v-text="computedProduct.title"
      />

      <p
        v-if="featuredCollection"
        class="
          product-card__featured-collection
          text-label-xs
          text-center
        "
        :style="{ animationDelay: animationDelay }"
        v-text="featuredCollection"
      />

      <product-prices
        class="text-body-3xs text-center"
        :class="$blockClass(blockClass, 'product-card-prices')"
        :available="computedProduct?.available || computedProduct.isComingSoon"
        block-class="product-card"
        :hidden-sale-price="hiddenSalePrice"
        :compare-at-price="computedProduct?.compareAtPrice?.amount"
        :price="computedProduct?.price?.amount"
        :show-red-price="priceInRed"
        :style="{ animationDelay: animationDelay }"
      />

      <pc-colour-swatches
        v-if="placement !== 'cart-drawer'"
        :loading="loading"
        :swatches="swatches"
        :product="computedProduct"
        :colour-family-size="colourFamilySize"
        :hidden-swatches-count="hiddenSwatchesCount"
        @product-update="updateProduct"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number} index - Product card's forloop index.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Number} colorCount - Optional color count displayed below the price.
 * @param {Object} badge - The badge object.
 * @param {String} badge.label - The displayed badge text.
 * @param {String} badge.color - The displayed badge color.
 * @param {String} badge.bgColor - The displayed badge background color.
 * @param {String} badge.type - The badge type, `sale` is currently only supported.
 * @param {String} imageOrder - Identify image order between Modal / Flat Shot.
 * @param {String} imageUseAlt - Check if the product card displays Alt-Tag image.
 * @param {Boolean} large - Enables the large card styles.
 * @param {Boolean} showQuickAdd - Determines whether the quick add bar shows on card hover.
 * @param {String} placement - Where recommendations rendered.
 * @param {Boolean} bigCard - Renders the product card in an almost square shape.
 *
 */
import { mapActions, mapState, mapGetters } from 'vuex'

import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import SizeSelector from '~async/size-selector/size-selector'
import PcColourSwatches from '~async/pc-colour-swatches/pc-colour-swatches'

import { getHiddenSalePrice } from '~/helpers/general'
import IconBag from '~icons/misc/bag.svg'

export default {
  name: 'ProductCard',

  components: {
    IconBag,
    PcColourSwatches,
    ProductPrices,
    ResponsiveImage,
    SizeSelector,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    index: {
      type: Number,
      default: 0,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    badge: {
      type: Object,
      default: () => ({}),
    },
    imageOrder: {
      type: String,
      default: '',
    },
    imageUseAlt: {
      type: Boolean,
      default: false,
    },
    large: {
      type: Boolean,
      default: false,
    },
    namespace: {
      type: String,
      default: 'collection',
    },
    showQuickAdd: {
      type: Boolean,
      default: true,
    },
    placement: {
      type: String,
      default: '',
    },
    alternativeImage: {
      type: Object,
      default: () => ({}),
    },
    bigCard: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['product-card-clicked'],

  data() {
    return {
      cardHover: false,
      imageKey: 0,
      loadSecondaryImage: false,
      showSecondaryImage: false,
      productData: false,
      currentVariantId: false,
      swatches: [],
      hiddenSwatchesCount: 0,
    }
  },

  computed: {

    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    ...mapGetters({
      getRelatedProductsByTag: 'product-card/getRelatedProductsByTag',
      getShopifyProductsById: 'product-card/getShopifyProductsById',
      isTagLoading: 'product-card/isTagLoading',
    }),

    computedProduct() {
      return this.productData || this.product
    },

    tag() {
      return this.getFamilyTag()
    },

    relatedProducts() {
      return this.getRelatedProductsByTag(this.tag)
    },

    loading() {
      return this.isTagLoading(this.tag) || this.product === false
    },

    mergedProduct() {
      const shopifyProduct = this.getShopifyProductsById(this.computedProduct?.id) || {}

      return {
        ...this.computedProduct,
        ...shopifyProduct,
      }
    },

    /**
     * Returns the current products swatch name.
     * @returns {String}
     */
    colourFamilySize() {
      const sizeMetafield = this.mergedProduct?.metafields?.find(field => field?.name === 'colour_family_size')

      if (!sizeMetafield) {
        return ''
      }

      return sizeMetafield.value || ''
    },

    /**
     * Returns the current product featured collection.
     * @returns {String}
     */
    featuredCollection() {
      const featuredCollection = this.mergedProduct?.metafields?.find(field => field?.name === 'featured_collection')

      if (!featuredCollection) {
        return ''
      }

      return featuredCollection.value || ''
    },

    /**
     * Return image order type
     * @return {Boolean}
     */
    isFlatImage() {
      return this.imageOrder === 'flat'
    },

    /**
     * Remove alt-tag image from product images.
     * @returns {Boolean, Array}
     */
    imagesWithoutAlt() {
      return this.computedProduct?.images?.filter((image) => {
        return !image.alt?.includes('alt_landing_image')
      })
    },

    /**
     * Returns the image which has landing page alt tag.
     * @returns {Boolean, Object}
     */
    altImage() {
      return this.computedProduct?.images?.find((image) => {
        return image.alt?.includes('alt_landing_image')
      })
    },

    /**
     * Returns the primary image.
     *
     * @returns {Object}
     */
    imagePrimary() {
      if (this.placement === 'wiyw') {
        return this.alternativeImage
      }

      if (!this.computedProduct || !this.computedProduct.images) {
        return false
      }

      if (this.imageUseAlt && this.altImage) {
        return this.altImage
      }

      if (this.isFlatImage) {
        return this.imagesWithoutAlt[this.imagesWithoutAlt.length - 1]
      }

      return this.imagesWithoutAlt[0]
    },

    /**
     * Returns the secondary image.
     *
     * @returns {Object}
     */
    imageSecondary() {
      if (this.computedProduct?.images?.length < 2) {
        return false
      }

      if (this.imageUseAlt) {
        return false
      }

      if (this.isFlatImage) {
        return this.imagesWithoutAlt[0]
      }

      return this.imagesWithoutAlt[this.imagesWithoutAlt.length - 1]
    },

    /**
     * Returns the title for the primary image.
     * - If the image object itself has an image, return it.
     * - Otherwise, return the product title.
     *
     * @returns {String}
     */
    imagePrimaryTitle() {
      if (this.imagePrimary?.alt) {
        return this.imagePrimary.alt
      }

      return this.computedProduct?.title
    },

    /**
     * Returns the title for the secondary image.
     * - If the image object itself has an image, return it.
     * - Otherwise, return the product title.
     *
     * @returns {String}
     */
    imageSecondaryTitle() {
      if (this.imageSecondary?.alt) {
        return this.imageSecondary.alt
      }

      return this.computedProduct?.title
    },

    /**
     * Returns the available colourways count for the product, from its metafields.
     * @returns {Number}
     */
    colorCount() {
      const count = this.computedProduct?.metafields?.find(field => field?.name === 'colour_family_size')

      return count?.value ?? 0
    },

    /**
     * Compute animation delay.
     * @returns {String}
     */
    animationDelay() {
      return this.$timing('quick', { index: this.index, unit: 's' })
    },

    /**
     * Determines whether the price should be shown in red.
     * @returns {Boolean}
     */
    priceInRed() {
      return this.computedProduct?.tags?.includes('red-price') || false
    },

    /**
     * Returns the hidden sale price.
     * @returns {String}
     */
    hiddenSalePrice() {
      return getHiddenSalePrice(this.computedProduct?.tags, this.$variable('store.currency'))
    },

    productUrl() {
      const url = this.computedProduct.url

      if (!this.currentVariantId) {
        return url
      }

      return url.includes("?") ?
        `${url}&variant=${this.currentVariantId}` :
        `${url}?variant=${this.currentVariantId}`
    },

    /**
     * Returns the colour family tag from the product tags.
     * @returns {String}
     */
    colourFamily() {
      if (!this.product) {
        return ""
      }

      const colourTag = this.product.tags.find(
        tag => tag.includes('ProductColourFamily-'),
      )

      if (!colourTag) {
        return ""
      }

      return colourTag
    },

    /**
     * Returns the size from the product tags.
     * @returns {String}
     */
    currentSize() {
      if (!this.product?.tags) {
        return ""
      }

      const size = this.product.tags.find(tag => tag.includes('ProductSize-'))

      if (!size) {
        return ""
      }

      const sizeText = size.split('-')[1] || false
      return sizeText
    },
    visibleSwatchesCount() {
      return this.isMobile ? 6 : 8
    },
  },

  watch: {
    'computedProduct.id': function(newVal, oldVal) {
      if (newVal == oldVal) {
        return
      }

      this.imageKey++
    },

    visibleSwatchesCount(newVal, oldVal) {
      if(newVal !== oldVal) {
        this.constructSwatches()
      }
    },
  },

  async mounted() {
    // Fetch products for this swatch's tag
    await this.fetchProductsByTag(`${this.tag}`)
    this.constructSwatches()
  },

  methods: {
    ...mapActions({
      fetchProductsByTag: 'product-card/fetchProductsByTag',
      setQuickAddProduct: 'product-card/setQuickAddProduct',
      setCurrentSize: 'product-card/setCurrentSize',
      setColourFamily: 'product-card/setColourFamily',
    }),

    handleAddToCart() {
      this.setCurrentSize(this.currentSize)
      this.setColourFamily(this.colourFamily)
      this.setQuickAddProduct(this.computedProduct)
    },

    /**
     * Sets the product ID to session storage, for use when returning to PLP.
     */
    selectProduct(){
      sessionStorage.setItem('selected_product_id', this.computedProduct.id)
      this.$emit('product-card-clicked', this.computedProduct.id)
    },

    /**
     * Updates the product.
     * @param {Object} - Product object.
     */
    updateProduct(swatchProduct) {
      if (!swatchProduct) {
        return
      }

      let queryID = ''
      let productUrl = swatchProduct.url

      try {
        queryID = new URL(this.product.url, location.origin).searchParams.get('queryID')

        if(queryID) {
          productUrl = productUrl.includes("?") ?
            `${productUrl}&queryID=${queryID}` :
            `${productUrl}?queryID=${queryID}`
        }
      } catch (e) {
        console.error('Error getting queryID', e)
      }

      this.productData = {
        ...swatchProduct,
        url: productUrl,
      }
    },

    /**
     * Updates the currentVariantId when a new variant is selected.
     * @param {Object} variant - Product variant object.
     */
    updateVariant(variant) {
      if (!variant) {
        this.currentVariantId = 0
        return
      }

      this.selectProduct()
      this.currentVariantId = variant.id
      window.location = this.productUrl
    },

    constructSwatches() {
      const sizeProducts = this.filterProductsBySize(this.relatedProducts)
      const colourProducts = this.filterProductsByColour(sizeProducts)
      const validSwatches = colourProducts.filter(swatch => swatch.available || swatch.handle === this.product.handle)

      // Find the active swatch corresponding to the current product.
      const activeSwatch = validSwatches.find(swatch => swatch.id == this.product.id)

      // Get the initial set of swatches to display.
      let displayedSwatches = validSwatches.slice(0, this.visibleSwatchesCount)

      // Check if the active swatch is already included in the displayed set.
      const activeIncluded = displayedSwatches.some(swatch => swatch.id == this.product.id)

      // If the active swatch is included, or there isn't one, return the current set.
      if (activeIncluded || !activeSwatch) {
        this.swatches = displayedSwatches
        this.hiddenSwatchesCount = validSwatches.length - displayedSwatches.length
        return
      }

      // Otherwise, prepend the active swatch and limit the set to the maximum count.
      displayedSwatches = [activeSwatch, ...validSwatches].slice(0, this.visibleSwatchesCount)
      this.swatches = displayedSwatches
      this.hiddenSwatchesCount = validSwatches.length - displayedSwatches.length
    },

    /**
     * Filters the products based on the current product size.
     * @param {Array} products - Array of products.
     * @returns {Array}
     */
    filterProductsBySize(products) {
      const size = `ProductSize-${this.currentSize}`

      return products.filter(product => {
        if (product.tags.find(tag => tag === size)) {
          return product
        }
      })
    },

    /**
     * Filters the products by current product colour family.
     * @param {Array} products - Array of products.
     * @returns {Array}
     */
    filterProductsByColour(products) {
      return products.filter(product => {
        if (
          product?.tags.find(tag => tag === this.colourFamily) &&
          product.metafields?.find(field => field.name === 'swatch_code')
        ) {
          return product
        }
      })
    },

    /**
     * Returns the colour family tag from the product tags.
     * @returns {String}
     */
    getFamilyTag() {
      if (!this.product) {
        return ''
      }

      const colourTag = this.product.tags.find(
        tag => tag.includes('ProductColourFamily-'),
      )

      const sizeTag = this.product.tags.find(
        tag => tag.includes('ProductSizeFamily-'),
      )

      return colourTag || sizeTag || ''
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
